import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './NewDashboard.css';
import './AdminDashboard.css';
import regionalApiService from '../services/RegionalApiService';
import usuarioService from '../services/UsuarioService';
import ActiveRegionalBadge from './common/ActiveRegionalBadge';
import UserAccountMenu from './common/UserAccountMenu';
import { useAuth } from '../contexts/AuthContext';

const AdminCreateRegionalAdmin = () => {
  const navigate = useNavigate();
  const { regionalId } = useParams();
  const { user } = useAuth();

  const [regionalName, setRegionalName] = useState('');
  const [form, setForm] = useState({
    email: '',
    dni: '',
    nombre: '',
    apellido: '',
    telefono: ''
  });
  const [admins, setAdmins] = useState([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [adminsSearch, setAdminsSearch] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false, admin: null });
  const [editForm, setEditForm] = useState({
    email: '',
    dni: '',
    nombre: '',
    apellido: '',
    telefono: ''
  });
  const [toggleModal, setToggleModal] = useState({ open: false, admin: null, nextActivo: true });
  const [resetModal, setResetModal] = useState({ open: false, admin: null });
  const [resetResult, setResetResult] = useState({ open: false, ok: true, message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  const filteredAdmins = useMemo(() => {
    if (!adminsSearch.trim()) return admins;
    const term = adminsSearch.toLowerCase();
    return admins.filter((u) => {
      const nombre = `${u.nombre || ''} ${u.apellido || ''}`.trim().toLowerCase();
      const display = (u.displayName || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return nombre.includes(term) || display.includes(term) || email.includes(term);
    });
  }, [admins, adminsSearch]);

  useEffect(() => {
    const loadRegional = async () => {
      if (!regionalId) return;
      try {
        const resp = await regionalApiService.getRegionalById(regionalId);
        const reg = (resp.regionales || resp.Regionales || [])[0];
        setRegionalName(reg?.nombre || reg?.Nombre || 'Regional');
      } catch (error) {
        setRegionalName('Regional');
        setMessage(error.message || 'No pudimos cargar el regional.');
        setMessageType('error');
      }
    };
    loadRegional();
  }, [regionalId]);

  const loadAdmins = async () => {
    if (!regionalId) return;
    setAdminsLoading(true);
    setAdminsError('');
    try {
      const lista = await usuarioService.getAdminRegionalUsersPorRegional(regionalId);
      setAdmins(Array.isArray(lista) ? lista : []);
    } catch (error) {
      setAdminsError(error.message || 'No pudimos cargar los administradores.');
    } finally {
      setAdminsLoading(false);
    }
  };

  useEffect(() => {
    loadAdmins();
  }, [regionalId]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleEditChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const openEditModal = (admin) => {
    setEditForm({
      email: admin.email || admin.Email || '',
      dni: admin.dni || admin.Dni || '',
      nombre: admin.nombre || admin.Nombre || '',
      apellido: admin.apellido || admin.Apellido || '',
      telefono: admin.telefono || admin.Telefono || ''
    });
    setEditModal({ open: true, admin });
  };

  const closeEditModal = () => {
    setEditModal({ open: false, admin: null });
  };

  const handleSaveEdit = async () => {
    if (!editModal.admin || !regionalId) return;
    if (!editForm.email.trim()) {
      setAdminsError('Completa email para continuar.');
      return;
    }
    setActionLoading(true);
    setAdminsError('');
    setMessage('Actualizando administrador...');
    setMessageType('info');
    try {
      const displayName = `${editForm.nombre || ''} ${editForm.apellido || ''}`.trim() || editForm.email;
      await usuarioService.updateAdminRegionalUser(editModal.admin.id || editModal.admin.Id, {
        ...editForm,
        displayName,
        idRegional: regionalId
      });
      await loadAdmins();
      closeEditModal();
      setMessage('Administrador actualizado correctamente.');
      setMessageType('success');
    } catch (error) {
      setAdminsError(error.message || 'No pudimos actualizar el administrador.');
      setMessage(error.message || 'No pudimos actualizar el administrador.');
      setMessageType('error');
    } finally {
      setActionLoading(false);
    }
  };

  const openToggleModal = (admin) => {
    const activo = typeof admin.activo === 'boolean' ? admin.activo : admin.Activo;
    setToggleModal({ open: true, admin, nextActivo: !activo });
  };

  const closeToggleModal = () => {
    setToggleModal({ open: false, admin: null, nextActivo: true });
  };

  const handleToggleActivo = async () => {
    if (!toggleModal.admin) return;
    setActionLoading(true);
    setAdminsError('');
    try {
      await usuarioService.updateAdminRegionalActivo(toggleModal.admin.id || toggleModal.admin.Id, toggleModal.nextActivo);
      await loadAdmins();
      closeToggleModal();
    } catch (error) {
      setAdminsError(error.message || 'No pudimos actualizar el estado del administrador.');
    } finally {
      setActionLoading(false);
    }
  };

  const openResetModal = (admin) => {
    setResetModal({ open: true, admin });
  };

  const closeResetModal = () => {
    setResetModal({ open: false, admin: null });
  };

  const handleResetPassword = async () => {
    if (!resetModal.admin) return;
    setActionLoading(true);
    setAdminsError('');
    try {
      await usuarioService.resetAdminRegionalPassword(resetModal.admin.id || resetModal.admin.Id);
      closeResetModal();
      setResetResult({ open: true, ok: true, message: 'Contraseña reseteada: ahora será su DNI.' });
    } catch (error) {
      setResetResult({ open: true, ok: false, message: error.message || 'No pudimos resetear la contraseña.' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!regionalId) {
      setMessage('No se pudo determinar el regional.');
      setMessageType('error');
      return;
    }
    if (!form.email) {
      setMessage('Completa email para continuar.');
      setMessageType('error');
      return;
    }
    if (!form.dni.trim()) {
      setMessage('Completa DNI para continuar.');
      setMessageType('error');
      return;
    }

    setIsSubmitting(true);
    setMessage('Creando administrador...');
    setMessageType('info');
    try {
      const displayName = `${form.nombre || ''} ${form.apellido || ''}`.trim() || form.email;
      const passwordToSend = form.dni.trim();

      await usuarioService.createAdminRegionalUser({
        ...form,
        displayName,
        password: passwordToSend,
        idRegional: regionalId
      });

      setMessage('Administrador creado correctamente.');
      setMessageType('success');
      setForm({
        email: '',
        dni: '',
        nombre: '',
        apellido: '',
        telefono: ''
      });
      await loadAdmins();
    } catch (error) {
      setMessage(error.message || 'No pudimos crear el administrador.');
      setMessageType('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="admin-standalone">
      <header className="admin-standalone-header">
        <div>
          <p className="eyebrow">Administradores de regional</p>
          <h1>Alta de administrador</h1>
          <p className="subtitle">Regional: {regionalName}</p>
        </div>
        <div className="admin-standalone-actions">
          <ActiveRegionalBadge />
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => navigate('/admin/select-regional')}
          >
            <i className="fas fa-arrow-left" />
            <span>Volver a selección de regionales</span>
          </button>
          <UserAccountMenu />
        </div>
      </header>

      <div className="admin-dual-panel">
          <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-user-plus" />
              <span>Datos del administrador</span>
            </div>
          </div>
          <div className="card-content">
            <p style={{ marginTop: 0, color: '#475569' }}>
              Completa los datos requeridos. <strong>La contraseña inicial será el DNI</strong>; el usuario podrá cambiarla luego.
            </p>
            <form onSubmit={handleSubmit} className="prof-create-form">
              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="admin.regional@ejemplo.com"
                />
              </div>
              <div className="form-field">
                <label>DNI</label>
                <input
                  type="text"
                  value={form.dni}
                  onChange={(e) => handleChange('dni', e.target.value)}
                  placeholder="12345678"
                />
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={form.nombre}
                    onChange={(e) => handleChange('nombre', e.target.value)}
                    placeholder="Nombre"
                  />
                </div>
                <div className="form-field">
                  <label>Apellido</label>
                  <input
                    type="text"
                    value={form.apellido}
                    onChange={(e) => handleChange('apellido', e.target.value)}
                    placeholder="Apellido"
                  />
                </div>
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Teléfono</label>
                  <input
                    type="text"
                    value={form.telefono}
                    onChange={(e) => handleChange('telefono', e.target.value)}
                    placeholder="351123456"
                  />
                </div>
              </div>

              {message && (
                <div
                  className="modal-message"
                  style={{
                    marginTop: '0.5rem',
                    borderColor: messageType === 'success' ? '#bbf7d0' : messageType === 'error' ? '#fecaca' : 'var(--gray-200)',
                    background: messageType === 'success' ? '#dcfce7' : messageType === 'error' ? '#fee2e2' : '#f8fafc',
                    color: messageType === 'error' ? '#7f1d1d' : messageType === 'success' ? '#0f5132' : 'var(--gray-800)'
                  }}
                >
                  {isSubmitting && <i className="fas fa-spinner fa-spin" style={{ marginRight: '0.35rem' }} />}
                  {message}
                </div>
              )}

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
                  {isSubmitting ? <i className="fas fa-spinner fa-spin" /> : <i className="fas fa-save" />}
                  <span>Crear administrador</span>
                </button>
              </div>
            </form>
          </div>
        </div>

        <div className="content-card">
          <div className="card-header">
            <div className="card-title">
              <i className="fas fa-users" />
              <span>Administradores del regional</span>
            </div>
            <div className="professor-actions">
              <div className="professor-search">
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  type="text"
                  placeholder="Buscar por nombre o email"
                  value={adminsSearch}
                  onChange={(e) => setAdminsSearch(e.target.value)}
                />
              </div>
              <button className="refresh-btn" type="button" onClick={loadAdmins} disabled={adminsLoading}>
                <i className={`fas ${adminsLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'}`} />
              </button>
            </div>
          </div>
          <div className="card-content professors-table">
            {adminsLoading && (
              <div className="data-table-loading">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin"></i>
                </div>
                <p>Cargando administradores...</p>
              </div>
            )}

            {!adminsLoading && adminsError && (
              <div className="data-table-empty">
                <div className="empty-icon">
                  <i className="fas fa-exclamation-triangle"></i>
                </div>
                <p>{adminsError}</p>
                <button className="refresh-btn" type="button" onClick={loadAdmins}>
                  <i className="fas fa-sync-alt" /> Reintentar
                </button>
              </div>
            )}

            {!adminsLoading && !adminsError && filteredAdmins.length === 0 && (
              <div className="data-table-empty">
                <div className="empty-icon">
                  <i className="fas fa-users-slash"></i>
                </div>
                <p>No hay administradores cargados.</p>
              </div>
            )}

              {!adminsLoading && !adminsError && filteredAdmins.length > 0 && (
                <div className="data-table-wrapper">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Nombre</th>
                        <th>Email</th>
                        <th>Teléfono</th>
                        <th className="text-center width-sm">Estado</th>
                        <th className="text-center width-sm">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAdmins.map((u) => {
                        const nombre = `${u.nombre || ''} ${u.apellido || ''}`.trim() || u.displayName || '-';
                        const activo = typeof u.activo === 'boolean' ? u.activo : u.Activo;
                        return (
                          <tr key={u.id || u.Id || u.email}>
                            <td>{nombre}</td>
                            <td>{u.email || '-'}</td>
                            <td>{u.telefono || '-'}</td>
                            <td className="text-center">
                              <span className={`status-chip ${activo ? 'success' : 'danger'}`}>
                                {activo ? 'Activo' : 'Inactivo'}
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="action-buttons">
                                <button
                                  type="button"
                                  className="action-edit-btn"
                                  onClick={() => openEditModal(u)}
                                  title="Editar administrador"
                                >
                                  <i className="fas fa-edit"></i>
                                </button>
                                <button
                                  type="button"
                                  className="action-reset-btn"
                                  onClick={() => openResetModal(u)}
                                  title="Resetear contraseña"
                                >
                                  <i className="fas fa-key"></i>
                                </button>
                                <button
                                  type="button"
                                  className={`action-toggle-btn ${activo ? 'danger' : 'success'}`}
                                  onClick={() => openToggleModal(u)}
                                  disabled={actionLoading}
                                  title={activo ? 'Desactivar administrador' : 'Activar administrador'}
                                >
                                  <i className={`fas ${activo ? 'fa-ban' : 'fa-check-circle'}`}></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
          </div>
        </div>
      </div>

      {editModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-user-edit" />
            </div>
            <h3>Editar administrador</h3>
            <p>Actualiza los datos del administrador del regional.</p>
            <div className="modal-form">
              <div className="form-field">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => handleEditChange('email', e.target.value)}
                />
              </div>
              <div className="form-field">
                <label>DNI</label>
                <input
                  type="text"
                  value={editForm.dni}
                  onChange={(e) => handleEditChange('dni', e.target.value)}
                />
              </div>
              <div className="form-grid">
                <div className="form-field">
                  <label>Nombre</label>
                  <input
                    type="text"
                    value={editForm.nombre}
                    onChange={(e) => handleEditChange('nombre', e.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label>Apellido</label>
                  <input
                    type="text"
                    value={editForm.apellido}
                    onChange={(e) => handleEditChange('apellido', e.target.value)}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Teléfono</label>
                <input
                  type="text"
                  value={editForm.telefono}
                  onChange={(e) => handleEditChange('telefono', e.target.value)}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeEditModal} disabled={actionLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleSaveEdit} disabled={actionLoading}>
                {actionLoading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {toggleModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${toggleModal.nextActivo ? 'fa-check-circle' : 'fa-ban'}`} />
            </div>
            <h3>{toggleModal.nextActivo ? 'Activar administrador' : 'Desactivar administrador'}</h3>
            <p>
              {toggleModal.nextActivo
                ? 'El administrador volverá a estar activo en el regional.'
                : 'El administrador dejará de estar activo en el regional.'}
            </p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeToggleModal} disabled={actionLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleToggleActivo} disabled={actionLoading}>
                {actionLoading ? 'Actualizando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetModal.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className="fas fa-key" />
            </div>
            <h3>Resetear contraseña</h3>
            <p>La contraseña se establecerá automáticamente al DNI registrado.</p>
            <div className="modal-actions">
              <button type="button" className="btn-secondary" onClick={closeResetModal} disabled={actionLoading}>
                Cancelar
              </button>
              <button type="button" className="btn-primary" onClick={handleResetPassword} disabled={actionLoading}>
                {actionLoading ? 'Reseteando...' : 'Resetear'}
              </button>
            </div>
          </div>
        </div>
      )}

      {resetResult.open && (
        <div className="modal-overlay">
          <div className="modal-card">
            <div className="modal-icon">
              <i className={`fas ${resetResult.ok ? 'fa-check-circle' : 'fa-exclamation-triangle'}`} />
            </div>
            <h3>{resetResult.ok ? 'Contraseña reseteada' : 'No se pudo resetear'}</h3>
            <p>{resetResult.message}</p>
            <div className="modal-actions">
              <button
                type="button"
                className="btn-primary"
                onClick={() => setResetResult({ open: false, ok: true, message: '' })}
              >
                Entendido
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminCreateRegionalAdmin;
